import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Divider,
  Modal,
  TextField,
  Banner,
  Spinner,
} from '@shopify/polaris';

import { useStore } from '../providers/StoreProvider';
import { supportApi } from '../utils/api';
import AppLayout from '../components/AppLayout';

const STATUS_BADGE = {
  pending: { tone: 'warning', label: 'Pending' },
  in_progress: { tone: 'info', label: 'In Progress' },
  solved: { tone: 'success', label: 'Solved' },
};

function Settings() {
  const navigate = useNavigate();
  const { store, billing } = useStore();

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportResult, setSupportResult] = useState(null);
  const [latestTicket, setLatestTicket] = useState(null);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  const loadTickets = useCallback(async () => {
    try {
      const data = await supportApi.getTickets();
      setLatestTicket(data.ticket || null);
    } catch {
      // silently fail
    }
    setTicketsLoading(false);
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSendSupport = useCallback(async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) return;

    setSupportSending(true);
    setSupportResult(null);

    try {
      await supportApi.send(supportSubject.trim(), supportMessage.trim());
      setSupportResult({ success: true });
      setSupportSubject('');
      setSupportMessage('');
      loadTickets();
    } catch {
      setSupportResult({ success: false });
    }

    setSupportSending(false);
  }, [supportSubject, supportMessage, loadTickets]);

  const handleCloseSupportModal = useCallback(() => {
    setSupportModalOpen(false);
    setSupportResult(null);
  }, []);

  return (
    <AppLayout>
      <Page title="Settings">
        <Layout>
          {/* Store Information */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">Store Information</Text>

                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text>Store Name</Text>
                    <Text fontWeight="semibold">{store?.shop_name || '-'}</Text>
                  </InlineStack>

                  <InlineStack align="space-between">
                    <Text>Domain</Text>
                    <Text fontWeight="semibold">{store?.shop_domain || '-'}</Text>
                  </InlineStack>

                  <InlineStack align="space-between">
                    <Text>Currency</Text>
                    <Text fontWeight="semibold">{store?.shop_currency || 'USD'}</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Plan Information */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd">Your Plan</Text>
                  <Badge tone={billing?.is_premium ? 'success' : 'default'}>
                    {billing?.is_premium ? 'Premium' : 'Basic'}
                  </Badge>
                </InlineStack>

                {billing?.is_in_trial && (
                  <Text tone="subdued">
                    Premium ends: {new Date(billing.trial_ends_at).toLocaleDateString()}
                    {billing?.trial_days_remaining != null &&
                      ` (${billing.trial_days_remaining} day${billing.trial_days_remaining !== 1 ? 's' : ''} remaining)`
                    }
                  </Text>
                )}

                <Divider />

                <Text variant="headingSm">Your Features</Text>

                <BlockStack gap="100">
                  {billing?.features && Object.entries(billing.features).map(([key, value]) => (
                    <InlineStack key={key} align="space-between">
                      <Text>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
                      <Badge tone={value ? 'success' : 'default'}>
                        {value === true ? '✓' : value === false ? '✗' : value}
                      </Badge>
                    </InlineStack>
                  ))}
                </BlockStack>

                {!billing?.is_premium && (
                  <Button onClick={() => navigate('/app/billing')} variant="primary">
                    Upgrade to Premium
                  </Button>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* App Information & Support */}
          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="300">
                  <Text variant="headingMd">About</Text>
                  <Text>Jambofy Free Shipping Bar</Text>
                  <Text tone="subdued">Version 1.0.0</Text>

                  <Divider />

                  <Text variant="headingSm">Support</Text>
                  <Text tone="subdued">
                    Need help? Send us a message and we'll get back to you.
                  </Text>
                  <Button onClick={() => setSupportModalOpen(true)}>
                    Contact Support
                  </Button>
                </BlockStack>
              </Card>

              {/* Support Tickets */}
              <Card>
                <BlockStack gap="300">
                  <Text variant="headingMd">Latest Ticket</Text>
                  {ticketsLoading ? (
                    <InlineStack align="center">
                      <Spinner size="small" />
                    </InlineStack>
                  ) : !latestTicket ? (
                    <Text tone="subdued">No support tickets yet.</Text>
                  ) : (
                    <BlockStack gap="100">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="bodySm" fontWeight="semibold">
                          {latestTicket.subject}
                        </Text>
                        <InlineStack gap="200">
                          <Badge tone={(STATUS_BADGE[latestTicket.status] || STATUS_BADGE.pending).tone}>
                            {(STATUS_BADGE[latestTicket.status] || STATUS_BADGE.pending).label}
                          </Badge>
                          <Badge tone={latestTicket.email_delivered ? 'success' : 'warning'}>
                            {latestTicket.email_delivered ? 'Delivered' : 'Sending...'}
                          </Badge>
                        </InlineStack>
                      </InlineStack>
                      <Text variant="bodySm" tone="subdued">
                        {new Date(latestTicket.created_at).toLocaleDateString()}
                      </Text>
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>

        {/* Support Email Modal */}
        <Modal
          open={supportModalOpen}
          onClose={handleCloseSupportModal}
          title="Contact Support"
          primaryAction={{
            content: 'Send',
            onAction: handleSendSupport,
            loading: supportSending,
            disabled: !supportSubject.trim() || !supportMessage.trim(),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleCloseSupportModal,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {supportResult?.success && (
                <Banner tone="success" onDismiss={() => setSupportResult(null)}>
                  <p>Message sent! We'll get back to you soon.</p>
                </Banner>
              )}
              {supportResult?.success === false && (
                <Banner tone="critical" onDismiss={() => setSupportResult(null)}>
                  <p>Failed to send message. Please try again.</p>
                </Banner>
              )}
              <TextField
                label="Subject"
                value={supportSubject}
                onChange={setSupportSubject}
                placeholder="e.g. Bar not showing on storefront"
                maxLength={200}
                autoComplete="off"
              />
              <TextField
                label="Message"
                value={supportMessage}
                onChange={setSupportMessage}
                placeholder="Describe your issue or question..."
                multiline={5}
                maxLength={5000}
                autoComplete="off"
              />
              <Text tone="subdued" variant="bodySm">
                We'll reply to {store?.shop_email || 'your store email'}.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppLayout>
  );
}

export default Settings;
